import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  STAGE_LABELS, ORG_TYPE_LABELS, ORG_STATUS_LABELS, BUDGET_MODEL_LABELS,
  ROLE_LABELS, CHANNEL_LABELS, LEVEL_LABELS, GENDER_LABELS,
  FUNDING_LABELS, PAYMENT_LABELS, LOST_REASON_LABELS,
  ACTIVITY_LABELS, ACTIVITY_ICONS,
  money, shortDate, programName, contactName, teamColorStyle
} from './constants';
import { CrmSettingsContext } from './CrmLayout';
import './OrganizationDetail.css';

const options = (labels) => Object.entries(labels).map(([v, l]) => <option key={v} value={v}>{l}</option>);

// ---------------------------------------------------------------------------
// Quick log -- the whole point is that this takes seconds, not minutes.
// ---------------------------------------------------------------------------
const QuickLog = ({ organizationId, contacts, programs, seasons, onLogged }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    activity_type: 'call', summary: '', contact_id: '', season_id: '', follow_up_at: '', body: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.summary.trim()) return setError('Say what happened');
    try {
      setSaving(true);
      const season = seasons.find(s => String(s.id) === form.season_id);
      await api.post('/crm/activities', {
        organization_id: Number(organizationId),
        activity_type: form.activity_type,
        summary: form.summary.trim(),
        body: form.body.trim() || null,
        contact_id: form.contact_id ? Number(form.contact_id) : null,
        season_id: form.season_id ? Number(form.season_id) : null,
        program_id: season ? season.program_id : null,
        follow_up_at: form.follow_up_at || null,
      });
      setForm({ activity_type: 'call', summary: '', contact_id: '', season_id: '', follow_up_at: '', body: '' });
      setOpen(false);
      setError('');
      onLogged();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to log that');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="quick-log">
      <form onSubmit={submit}>
        <div className="log-top">
          <select value={form.activity_type} onChange={set('activity_type')} aria-label="Activity type">
            {options(ACTIVITY_LABELS)}
          </select>
          <input
            value={form.summary}
            onChange={set('summary')}
            onFocus={() => setOpen(true)}
            placeholder="What just happened?"
          />
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Logging…' : 'Log'}
          </button>
        </div>

        {open && (
          <div className="log-more">
            <div className="form-row">
              <label>
                Who
                <select value={form.contact_id} onChange={set('contact_id')}>
                  <option value="">—</option>
                  {contacts.filter(c => c.is_active).map(c => (
                    <option key={c.id} value={c.id}>
                      {[c.first_name, c.last_name].filter(Boolean).join(' ')}
                      {c.role ? ` (${ROLE_LABELS[c.role] || c.role})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                About
                <select value={form.season_id} onChange={set('season_id')}>
                  <option value="">—</option>
                  {seasons.map(s => (
                    <option key={s.id} value={s.id}>{programName(s)} · {s.year}</option>
                  ))}
                </select>
              </label>
              <label>
                Follow up
                <input type="date" value={form.follow_up_at} onChange={set('follow_up_at')} />
              </label>
            </div>
            <textarea value={form.body} onChange={set('body')} placeholder="Details (optional)" />
            {programs.length === 0 && (
              <p className="hint">Add a program below to attach this to a season.</p>
            )}
          </div>
        )}
        {error && <p className="form-error">{error}</p>}
      </form>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------
const Modal = ({ title, children, onClose }) => (
  <div className="crm-modal-backdrop" onClick={onClose}>
    <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
      <h2>{title}</h2>
      {children}
    </div>
  </div>
);

const ContactModal = ({ organizationId, contact, onClose, onSaved }) => {
  const [form, setForm] = useState(contact || {
    first_name: '', last_name: '', role: '', title: '', email: '', phone: '', mobile: '',
    preferred_channel: 'unknown', best_time: '', start_date: '', end_date: '',
    is_active: true, is_primary: false, notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm(prev => ({
    ...prev, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) return setError('First name is required');
    try {
      setSaving(true);
      const payload = {
        ...form,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      if (contact) await api.put(`/crm/contacts/${contact.id}`, payload);
      else await api.post('/crm/contacts', { ...payload, organization_id: Number(organizationId) });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save the contact');
      setSaving(false);
    }
  };

  return (
    <Modal title={contact ? 'Edit contact' : 'New contact'} onClose={onClose}>
      <form onSubmit={submit}>
        {error && <p className="form-error">{error}</p>}
        <div className="form-row">
          <label>First name<input value={form.first_name} onChange={set('first_name')} autoFocus /></label>
          <label>Last name<input value={form.last_name || ''} onChange={set('last_name')} /></label>
        </div>
        <div className="form-row">
          <label>
            Role
            <select value={form.role || ''} onChange={set('role')}>
              <option value="">—</option>
              {options(ROLE_LABELS)}
            </select>
          </label>
          <label>Title<input value={form.title || ''} onChange={set('title')} placeholder="Head Football Coach" /></label>
        </div>
        <div className="form-row">
          <label>Email<input type="email" value={form.email || ''} onChange={set('email')} /></label>
          <label>Phone<input value={form.phone || ''} onChange={set('phone')} /></label>
          <label>Mobile<input value={form.mobile || ''} onChange={set('mobile')} /></label>
        </div>
        <div className="form-row">
          <label>
            Prefers
            <select value={form.preferred_channel || 'unknown'} onChange={set('preferred_channel')}>
              {options(CHANNEL_LABELS)}
            </select>
          </label>
          <label>
            Best time
            <input value={form.best_time || ''} onChange={set('best_time')} placeholder="Before 8am, never 3rd period" />
          </label>
        </div>
        <div className="form-row">
          <label>Started<input type="date" value={form.start_date || ''} onChange={set('start_date')} /></label>
          <label>Left<input type="date" value={form.end_date || ''} onChange={set('end_date')} /></label>
        </div>
        <div className="checkbox-row">
          <label className="inline">
            <input type="checkbox" checked={!!form.is_primary} onChange={set('is_primary')} /> Primary contact
          </label>
          <label className="inline">
            <input type="checkbox" checked={form.is_active !== false} onChange={set('is_active')} /> Still here
          </label>
        </div>
        <label>Notes<textarea value={form.notes || ''} onChange={set('notes')} /></label>
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};

const ProgramModal = ({ organizationId, program, sports, contacts, onClose, onSaved }) => {
  const [form, setForm] = useState(program || {
    sport: sports[0]?.sport || 'Football', level: 'varsity', gender: 'coed',
    roster_size: '', primary_contact_id: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        sport: form.sport,
        level: form.level,
        gender: form.gender,
        roster_size: form.roster_size === '' ? null : Number(form.roster_size),
        primary_contact_id: form.primary_contact_id ? Number(form.primary_contact_id) : null,
        notes: form.notes || null,
      };
      if (program) await api.put(`/crm/programs/${program.id}`, payload);
      else await api.post('/crm/programs', { ...payload, organization_id: Number(organizationId) });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save the program');
      setSaving(false);
    }
  };

  const selected = sports.find(s => s.sport === form.sport);

  return (
    <Modal title={program ? 'Edit program' : 'New program'} onClose={onClose}>
      <form onSubmit={submit}>
        {error && <p className="form-error">{error}</p>}
        <div className="form-row">
          <label>
            Sport
            <select value={form.sport} onChange={set('sport')}>
              {sports.map(s => <option key={s.sport} value={s.sport}>{s.sport}</option>)}
            </select>
          </label>
          <label>
            Level
            <select value={form.level} onChange={set('level')}>{options(LEVEL_LABELS)}</select>
          </label>
          <label>
            Gender
            <select value={form.gender} onChange={set('gender')}>{options(GENDER_LABELS)}</select>
          </label>
        </div>
        <div className="form-row">
          <label>
            Roster size
            <input type="number" value={form.roster_size ?? ''} onChange={set('roster_size')} />
          </label>
          <label>
            Coach
            <select value={form.primary_contact_id || ''} onChange={set('primary_contact_id')}>
              <option value="">—</option>
              {contacts.filter(c => c.is_active).map(c => (
                <option key={c.id} value={c.id}>{[c.first_name, c.last_name].filter(Boolean).join(' ')}</option>
              ))}
            </select>
          </label>
        </div>
        {selected && (
          <p className="hint">
            Orders for {selected.sport} are typically written between month {selected.window_start_month} and
            month {selected.window_end_month}. New seasons pick that window up automatically.
          </p>
        )}
        <label>Notes<textarea value={form.notes || ''} onChange={set('notes')} /></label>
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};

const SeasonModal = ({ season, onClose, onSaved }) => {
  const [form, setForm] = useState({
    stage: season.stage,
    window_start: season.window_start ? season.window_start.slice(0, 10) : '',
    window_end: season.window_end ? season.window_end.slice(0, 10) : '',
    need_by_date: season.need_by_date ? season.need_by_date.slice(0, 10) : '',
    funding_type: season.funding_type || 'unknown',
    estimated_value: season.estimated_value ?? '',
    quoted_value: season.quoted_value ?? '',
    actual_value: season.actual_value ?? '',
    product_cost: season.product_cost ?? '',
    decoration_cost: season.decoration_cost ?? '',
    freight_cost: season.freight_cost ?? '',
    other_cost: season.other_cost ?? '',
    po_number: season.po_number || '',
    invoice_number: season.invoice_number || '',
    deposit_amount: season.deposit_amount ?? '',
    amount_paid: season.amount_paid ?? '',
    payment_status: season.payment_status || 'unbilled',
    lost_to: season.lost_to || '',
    lost_reason: season.lost_reason || '',
    next_action_at: season.next_action_at ? season.next_action_at.slice(0, 10) : '',
    next_action_note: season.next_action_note || '',
    notes: season.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));
  const num = (v) => (v === '' || v === null ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put(`/crm/seasons/${season.id}`, {
        ...form,
        window_start: form.window_start || null,
        window_end: form.window_end || null,
        need_by_date: form.need_by_date || null,
        next_action_at: form.next_action_at || null,
        estimated_value: num(form.estimated_value),
        quoted_value: num(form.quoted_value),
        actual_value: num(form.actual_value),
        product_cost: num(form.product_cost),
        decoration_cost: num(form.decoration_cost),
        freight_cost: num(form.freight_cost),
        other_cost: num(form.other_cost),
        deposit_amount: num(form.deposit_amount),
        amount_paid: num(form.amount_paid),
        lost_reason: form.lost_reason || null,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save the season');
      setSaving(false);
    }
  };

  const cost = ['product_cost', 'decoration_cost', 'freight_cost', 'other_cost']
    .reduce((sum, k) => sum + Number(form[k] || 0), 0);
  const margin = Number(form.actual_value || 0) - cost;

  return (
    <Modal title={`${programName(season)} · ${season.year}`} onClose={onClose}>
      <form onSubmit={submit}>
        {error && <p className="form-error">{error}</p>}

        <div className="form-row">
          <label>
            Stage
            <select value={form.stage} onChange={set('stage')}>{options(STAGE_LABELS)}</select>
          </label>
          <label>
            Funding
            <select value={form.funding_type} onChange={set('funding_type')}>{options(FUNDING_LABELS)}</select>
          </label>
        </div>

        <div className="form-row">
          <label>Window opens<input type="date" value={form.window_start} onChange={set('window_start')} /></label>
          <label>Window closes<input type="date" value={form.window_end} onChange={set('window_end')} /></label>
          <label>Needed in hand<input type="date" value={form.need_by_date} onChange={set('need_by_date')} /></label>
        </div>

        <h3>Money</h3>
        <div className="form-row">
          <label>Estimated<input type="number" step="0.01" value={form.estimated_value} onChange={set('estimated_value')} /></label>
          <label>Quoted<input type="number" step="0.01" value={form.quoted_value} onChange={set('quoted_value')} /></label>
          <label>Actual<input type="number" step="0.01" value={form.actual_value} onChange={set('actual_value')} /></label>
        </div>
        <div className="form-row">
          <label>Product cost<input type="number" step="0.01" value={form.product_cost} onChange={set('product_cost')} /></label>
          <label>Decoration<input type="number" step="0.01" value={form.decoration_cost} onChange={set('decoration_cost')} /></label>
          <label>Freight<input type="number" step="0.01" value={form.freight_cost} onChange={set('freight_cost')} /></label>
          <label>Other<input type="number" step="0.01" value={form.other_cost} onChange={set('other_cost')} /></label>
        </div>
        <p className="margin-preview">
          Cost <strong>{money(cost)}</strong> · Gross margin <strong className={margin < 0 ? 'negative' : ''}>{money(margin)}</strong>
          {Number(form.actual_value) > 0 && ` (${Math.round((margin / Number(form.actual_value)) * 100)}%)`}
        </p>

        <h3>Billing</h3>
        <div className="form-row">
          <label>PO number<input value={form.po_number} onChange={set('po_number')} /></label>
          <label>Invoice<input value={form.invoice_number} onChange={set('invoice_number')} /></label>
          <label>
            Status
            <select value={form.payment_status} onChange={set('payment_status')}>{options(PAYMENT_LABELS)}</select>
          </label>
        </div>
        <div className="form-row">
          <label>Deposit<input type="number" step="0.01" value={form.deposit_amount} onChange={set('deposit_amount')} /></label>
          <label>Paid to date<input type="number" step="0.01" value={form.amount_paid} onChange={set('amount_paid')} /></label>
        </div>

        {form.stage === 'lost' && (
          <>
            <h3>Loss</h3>
            <div className="form-row">
              <label>Lost to<input value={form.lost_to} onChange={set('lost_to')} placeholder="Competitor or district contract" /></label>
              <label>
                Why
                <select value={form.lost_reason} onChange={set('lost_reason')}>
                  <option value="">—</option>
                  {options(LOST_REASON_LABELS)}
                </select>
              </label>
            </div>
          </>
        )}

        <h3>Next step</h3>
        <div className="form-row">
          <label>Follow up on<input type="date" value={form.next_action_at} onChange={set('next_action_at')} /></label>
          <label>What<input value={form.next_action_note} onChange={set('next_action_note')} /></label>
        </div>

        <label>Notes<textarea value={form.notes} onChange={set('notes')} /></label>

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};

const OrgModal = ({ organization, viewer, onClose, onSaved }) => {
  const [form, setForm] = useState({ ...organization });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reps, setReps] = useState([]);

  useEffect(() => {
    if (!viewer.canSeeAll) return;
    api.get('/crm/reps').then(r => setReps(r.data.reps)).catch(() => {});
  }, [viewer.canSeeAll]);

  const set = (key) => (e) => setForm(prev => ({
    ...prev, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value
  }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put(`/crm/organizations/${organization.id}`, {
        ...form,
        enrollment: form.enrollment === '' ? null : Number(form.enrollment),
        bid_threshold: form.bid_threshold === '' ? null : Number(form.bid_threshold),
        owner_user_id: form.owner_user_id === '' ? null : form.owner_user_id,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save the account');
      setSaving(false);
    }
  };

  return (
    <Modal title="Edit account" onClose={onClose}>
      <form onSubmit={submit}>
        {error && <p className="form-error">{error}</p>}
        <label>Name<input value={form.name} onChange={set('name')} /></label>
        <div className="form-row">
          <label>Type<select value={form.org_type} onChange={set('org_type')}>{options(ORG_TYPE_LABELS)}</select></label>
          <label>Status<select value={form.status} onChange={set('status')}>{options(ORG_STATUS_LABELS)}</select></label>
        </div>
        <div className="form-row">
          <label>Mascot<input value={form.mascot || ''} onChange={set('mascot')} /></label>
          <label>Colors<input value={form.colors || ''} onChange={set('colors')} placeholder="Navy / Vegas Gold" /></label>
        </div>
        <div className="form-row swatch-row">
          <label>
            Primary swatch
            <input type="color" value={form.color_primary || '#1b2a5b'} onChange={set('color_primary')} />
          </label>
          <label>
            Second swatch
            <input type="color" value={form.color_secondary || '#c9a227'} onChange={set('color_secondary')} />
          </label>
          <button
            type="button"
            className="clear-swatches"
            onClick={() => setForm(prev => ({ ...prev, color_primary: '', color_secondary: '' }))}
          >
            Clear colors
          </button>
        </div>
        <div className="form-row">
          <label>Classification<input value={form.uil_classification || ''} onChange={set('uil_classification')} /></label>
          <label>Enrollment<input type="number" value={form.enrollment ?? ''} onChange={set('enrollment')} /></label>
        </div>
        <div className="form-row">
          <label>City<input value={form.city || ''} onChange={set('city')} /></label>
          <label>Phone<input value={form.phone || ''} onChange={set('phone')} /></label>
        </div>

        {viewer.canSeeAll && (
          <>
            <h3>Ownership</h3>
            <label>
              Account owner
              <select value={form.owner_user_id || ''} onChange={set('owner_user_id')}>
                <option value="">Unassigned</option>
                {reps.map(r => (
                  <option key={r.id} value={r.id}>
                    {[r.first_name, r.last_name].filter(Boolean).join(' ') || r.username}
                    {r.is_manager ? ' (manager)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <p className="hint">
              Everything under this account — programs, seasons, contacts, activity — follows its owner.
            </p>
          </>
        )}

        <h3>How money works here</h3>
        <label>
          Budget model
          <select value={form.budget_model || 'unknown'} onChange={set('budget_model')}>{options(BUDGET_MODEL_LABELS)}</select>
        </label>
        <div className="form-row">
          <label>Payment terms<input value={form.payment_terms || ''} onChange={set('payment_terms')} placeholder="Net 30" /></label>
          <label>Bid threshold<input type="number" step="0.01" value={form.bid_threshold ?? ''} onChange={set('bid_threshold')} /></label>
        </div>
        <div className="checkbox-row">
          <label className="inline"><input type="checkbox" checked={!!form.tax_exempt} onChange={set('tax_exempt')} /> Tax exempt</label>
          <label className="inline"><input type="checkbox" checked={!!form.po_required} onChange={set('po_required')} /> PO required</label>
        </div>

        <label>Notes<textarea value={form.notes || ''} onChange={set('notes')} /></label>
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// The account screen
// ---------------------------------------------------------------------------
const OrganizationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings, viewer } = useContext(CrmSettingsContext);

  const [data, setData] = useState(null);
  const [sports, setSports] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);

  const [editOrg, setEditOrg] = useState(false);
  const [contactModal, setContactModal] = useState(null);   // {} for new, contact for edit
  const [programModal, setProgramModal] = useState(null);
  const [seasonModal, setSeasonModal] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/crm/organizations/${id}`);
      setData(response.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
      else setError(err.response?.data?.message || 'Failed to load the account');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
    api.get('/crm/meta')
      .then(r => { setSports(r.data.sports); setCurrentYear(r.data.currentSeasonYear); })
      .catch(() => {});
  }, [fetchAll]);

  const addSeason = async (programId, year) => {
    try {
      await api.post('/crm/seasons', { program_id: programId, year });
      fetchAll();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Failed to add the season');
    }
  };

  const rollForward = async (seasonId) => {
    try {
      await api.post(`/crm/seasons/${seasonId}/rollover`, {});
      fetchAll();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Failed to roll forward');
    }
  };

  if (loading && !data) return <div className="crm-org-detail"><p className="loading">Loading…</p></div>;
  if (notFound) {
    return (
      <div className="crm-org-detail">
        <button type="button" className="back-link" onClick={() => navigate('/crm/organizations')}>← Accounts</button>
        <div className="panel">
          <h2>Account not found</h2>
          <p className="empty">
            This account either doesn't exist or belongs to another rep. Accounts are visible only
            to the person who owns them.
          </p>
        </div>
      </div>
    );
  }
  if (error) return <div className="crm-org-detail"><p className="error-banner">{error}</p></div>;
  if (!data) return null;

  const { organization: org, contacts, programs, seasons, activities, financials } = data;
  const seasonsByProgram = programs.reduce((acc, p) => {
    acc[p.id] = seasons.filter(s => s.program_id === p.id).sort((a, b) => b.year - a.year);
    return acc;
  }, {});

  const teamColors = settings.use_team_colors ? teamColorStyle(org) : null;

  const totalRevenue = financials.reduce((sum, r) => sum + Number(r.revenue || 0), 0);
  const totalMargin = financials.reduce((sum, r) => sum + Number(r.margin || 0), 0);
  const totalBalance = financials.reduce((sum, r) => sum + Number(r.balance || 0), 0);

  return (
    <div className="crm-org-detail">
      <button type="button" className="back-link" onClick={() => navigate('/crm/organizations')}>
        ← Accounts
      </button>

      <header className="org-header">
        <div className="org-identity-block">
          {teamColors && (
            <span className="team-crest" style={teamColors} aria-hidden="true">
              {(org.short_name || org.name || '?').charAt(0).toUpperCase()}
            </span>
          )}
          <div>
          <h1>{org.name}</h1>
          <p className="org-tags">
            <span className={`status-pill status-${org.status}`}>{ORG_STATUS_LABELS[org.status]}</span>
            {[ORG_TYPE_LABELS[org.org_type], org.uil_classification, org.city,
              org.enrollment ? `${org.enrollment} students` : null]
              .filter(Boolean).map(t => <span key={t} className="tag">{t}</span>)}
          </p>
          <p className="org-owner">
            {org.owner_first_name
              ? <>Owned by <strong>{[org.owner_first_name, org.owner_last_name].filter(Boolean).join(' ')}</strong></>
              : <span className="unassigned">Unassigned</span>}
          </p>
          {(org.mascot || org.colors) && (
            <p className="org-identity">
              {org.mascot && <strong>{org.mascot}</strong>}
              {org.colors && <span> · {org.colors}</span>}
            </p>
          )}
          </div>
        </div>
        <div className="header-actions">
          {org.phone && <a className="tap-btn" href={`tel:${org.phone}`}>Call office</a>}
          <button type="button" className="btn-secondary" onClick={() => setEditOrg(true)}>Edit</button>
        </div>
      </header>

      {(org.po_required || org.tax_exempt || org.bid_threshold || org.budget_model !== 'unknown' || org.payment_terms) && (
        <div className="money-rules">
          {org.budget_model !== 'unknown' && <span>{BUDGET_MODEL_LABELS[org.budget_model]}</span>}
          {org.po_required && <span>PO required</span>}
          {org.tax_exempt && <span>Tax exempt</span>}
          {org.payment_terms && <span>{org.payment_terms}</span>}
          {org.bid_threshold && <span>Bids over {money(org.bid_threshold)}</span>}
        </div>
      )}

      <div className="money-cards">
        <div className="money-card">
          <div className="value">{money(totalRevenue)}</div>
          <div className="label">Lifetime revenue</div>
        </div>
        <div className="money-card">
          <div className="value">{money(totalMargin)}</div>
          <div className="label">Gross margin</div>
        </div>
        <div className="money-card">
          <div className="value">{money(totalBalance)}</div>
          <div className="label">Outstanding balance</div>
        </div>
      </div>

      <QuickLog
        organizationId={id}
        contacts={contacts}
        programs={programs}
        seasons={seasons}
        onLogged={fetchAll}
      />

      <div className="detail-columns">
        <div className="col-main">
          <section className="panel">
            <header>
              <h2>Programs</h2>
              <button type="button" className="btn-small" onClick={() => setProgramModal({})}>Add program</button>
            </header>

            {programs.length === 0 ? (
              <p className="empty">
                No programs yet. A program is what actually buys — Football, Tennis, each with its own
                coach, budget, and buying month.
              </p>
            ) : programs.map(program => {
              const rows = seasonsByProgram[program.id] || [];
              const hasCurrent = rows.some(s => s.year === currentYear);
              return (
                <div key={program.id} className="program-block">
                  <div className="program-head">
                    <div>
                      <strong>{programName(program)}</strong>
                      <span className="program-sub">
                        {program.roster_size ? `${program.roster_size} athletes` : 'Roster size unknown'}
                        {contactName(program) ? ` · ${contactName(program)}` : ''}
                      </span>
                    </div>
                    <div className="program-actions">
                      {!hasCurrent && (
                        <button type="button" className="btn-small" onClick={() => addSeason(program.id, currentYear)}>
                          Start {currentYear}
                        </button>
                      )}
                      <button type="button" className="btn-small ghost" onClick={() => setProgramModal(program)}>Edit</button>
                    </div>
                  </div>

                  {rows.length === 0 ? (
                    <p className="empty small">No seasons yet — a prospect is a program with no season row.</p>
                  ) : (
                    <ul className="season-rows">
                      {rows.map(season => (
                        <li key={season.id}>
                          <button type="button" className="season-open" onClick={() => setSeasonModal(season)}>
                            <span className="season-year">{season.year}</span>
                            <span className={`stage-pill stage-${season.stage}`}>{STAGE_LABELS[season.stage]}</span>
                            <span className="season-window">
                              {season.window_start ? `${shortDate(season.window_start)} – ${shortDate(season.window_end)}` : 'No window'}
                            </span>
                            <span className="season-money">
                              {season.actual_value
                                ? money(season.actual_value)
                                : season.quoted_value
                                  ? `${money(season.quoted_value)} quoted`
                                  : season.estimated_value
                                    ? `${money(season.estimated_value)} est.`
                                    : '—'}
                            </span>
                            {season.gross_margin && Number(season.gross_margin) !== 0 ? (
                              <span className="season-margin">{money(season.gross_margin)} margin</span>
                            ) : null}
                          </button>
                          <button
                            type="button"
                            className="btn-small ghost"
                            title="Next year's row on this program"
                            onClick={() => rollForward(season.id)}
                          >
                            Roll to {season.year + 1}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </section>

          <section className="panel">
            <h2>Activity</h2>
            {activities.length === 0 ? (
              <p className="empty">Nothing logged yet.</p>
            ) : (
              <ul className="activity-feed">
                {activities.map(a => (
                  <li key={a.id}>
                    <span className="act-icon">{ACTIVITY_ICONS[a.activity_type] || '📝'}</span>
                    <div>
                      <div className="act-summary">{a.summary}</div>
                      <div className="act-meta">
                        {ACTIVITY_LABELS[a.activity_type]} · {shortDate(a.occurred_at)}
                        {contactName(a) ? ` · ${contactName(a)}` : ''}
                        {a.sport ? ` · ${a.sport}` : ''}
                        {a.user_first_name ? ` · ${a.user_first_name}` : ''}
                      </div>
                      {a.body && <div className="act-body">{a.body}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="col-side">
          <section className="panel">
            <header>
              <h2>Contacts</h2>
              <button type="button" className="btn-small" onClick={() => setContactModal({})}>Add</button>
            </header>
            {contacts.length === 0 ? (
              <p className="empty">No contacts yet.</p>
            ) : (
              <ul className="contact-list">
                {contacts.map(c => (
                  <li key={c.id} className={c.is_active ? '' : 'departed'}>
                    <div className="contact-top">
                      <strong>{[c.first_name, c.last_name].filter(Boolean).join(' ')}</strong>
                      {c.is_primary && <span className="primary-tag">Primary</span>}
                      {!c.is_active && <span className="gone-tag">Gone</span>}
                    </div>
                    <div className="contact-role">
                      {c.title || ROLE_LABELS[c.role] || ''}
                      {c.preferred_channel && c.preferred_channel !== 'unknown' &&
                        ` · prefers ${CHANNEL_LABELS[c.preferred_channel].toLowerCase()}`}
                    </div>
                    {c.best_time && <div className="contact-time">{c.best_time}</div>}
                    <div className="contact-actions">
                      {(c.mobile || c.phone) && <a className="tap-btn" href={`tel:${c.mobile || c.phone}`}>Call</a>}
                      {(c.mobile || c.phone) && <a className="tap-btn" href={`sms:${c.mobile || c.phone}`}>Text</a>}
                      {c.email && <a className="tap-btn" href={`mailto:${c.email}`}>Email</a>}
                      <button type="button" className="tap-btn ghost" onClick={() => setContactModal(c)}>Edit</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {financials.length > 0 && (
            <section className="panel">
              <h2>By year</h2>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr><th>Year</th><th>Revenue</th><th>Margin</th><th>Balance</th></tr>
                  </thead>
                  <tbody>
                    {financials.map(row => (
                      <tr key={row.year}>
                        <td>{row.year}</td>
                        <td>{money(row.revenue)}</td>
                        <td>{money(row.margin)}</td>
                        <td>{money(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {org.notes && (
            <section className="panel">
              <h2>Notes</h2>
              <p className="org-notes">{org.notes}</p>
            </section>
          )}
        </div>
      </div>

      {editOrg && <OrgModal organization={org} viewer={viewer} onClose={() => setEditOrg(false)} onSaved={() => { setEditOrg(false); fetchAll(); }} />}
      {contactModal && (
        <ContactModal
          organizationId={id}
          contact={contactModal.id ? contactModal : null}
          onClose={() => setContactModal(null)}
          onSaved={() => { setContactModal(null); fetchAll(); }}
        />
      )}
      {programModal && (
        <ProgramModal
          organizationId={id}
          program={programModal.id ? programModal : null}
          sports={sports}
          contacts={contacts}
          onClose={() => setProgramModal(null)}
          onSaved={() => { setProgramModal(null); fetchAll(); }}
        />
      )}
      {seasonModal && (
        <SeasonModal
          season={seasonModal}
          onClose={() => setSeasonModal(null)}
          onSaved={() => { setSeasonModal(null); fetchAll(); }}
        />
      )}
    </div>
  );
};

export default OrganizationDetail;
